import { useEffect, useMemo, useRef, useState } from "react";
import { Paperclip, Trash2, Download, Plus, ChevronDown, FileText } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api, { API, fmtIDR, fmtDate } from "@/lib_app/api";
import Avatar from "@/components/app/Avatar";
import { DOC_CATEGORIES } from "@/components/app/InvoiceDocs";

const SOURCE_META = {
  project: { label: "Proyek", tint: "bg-[#E5EEFA] text-[#3B4A63] border-[#B0C7EA]" },
  quotation: { label: "Quotation", tint: "bg-[#E6E2F4] text-[#4A3B77] border-[#CFC7EA]" },
  invoice: { label: "Invoice", tint: "bg-[#E4F9EC] text-[#3f7a52] border-[#B7E7C7]" },
  po: { label: "Purchase Order", tint: "bg-[#FFF4CC] text-[#7A6633] border-[#FFDB52]" },
};

const fileTint = (name) => {
  const ext = (name || "").split(".").pop()?.toLowerCase();
  if (ext === "pdf") return { label: "PDF", bg: "bg-[#FDE7E9]", fg: "text-[#CE6968]" };
  if (["xls", "xlsx", "csv"].includes(ext)) return { label: "XLS", bg: "bg-[#E4F9EC]", fg: "text-[#3EB876]" };
  if (["doc", "docx"].includes(ext)) return { label: "DOC", bg: "bg-[#DDEBFB]", fg: "text-[#2F4B70]" };
  if (["jpg", "jpeg", "png", "gif", "webp"].includes(ext)) return { label: "IMG", bg: "bg-[#E6E2F4]", fg: "text-[#4A3B77]" };
  return { label: "FILE", bg: "bg-[#F4F5F7]", fg: "text-[#626061]" };
};

const catLabel = (key) => (DOC_CATEGORIES.find((c) => c.key === key) || DOC_CATEGORIES[3]).label;

export default function ProjectAttachmentsBoard({
  project, invoices = [], invoiceAttachments = [], userMap = {},
  canEdit = false, canDeleteInvoiceDoc = false, attaching = false,
  onUploadProject, onOpenProject, onDeleteProject,
  onUploadInvoice, onOpenInvoiceDoc, onDeleteInvoiceDoc,
}) {
  const [quotations, setQuotations] = useState([]);
  const [pos, setPos] = useState([]);
  const [poFiles, setPoFiles] = useState([]);
  const [filter, setFilter] = useState("all");
  const [panelOpen, setPanelOpen] = useState(false);
  const [target, setTarget] = useState("project");
  const [invoiceId, setInvoiceId] = useState("");
  const [category, setCategory] = useState("scan_invoice");
  const [busy, setBusy] = useState(false);
  const projectFileRef = useRef(null);
  const invoiceFileRef = useRef(null);

  useEffect(() => {
    if (!project?.prospect_id) { setQuotations([]); return; }
    api.get("/purchase-orders", { params: { project_id: project.id } })
      .then((r) => setPos(r.data || [])).catch(() => {});
    api.get("/quotations", { params: { prospect_id: project.prospect_id } })
      .then((r) => setQuotations(r.data || []))
      .catch(() => setQuotations([]));
  }, [project?.prospect_id]);

  const pickableInvoices = useMemo(() => invoices.filter((i) => i.status !== "void"), [invoices]);
  useEffect(() => {
    if (!project?.id) return;
    api.get(`/projects/${project.id}/po-attachments`).then((r) => setPoFiles(r.data || [])).catch(() => setPoFiles([]));
  }, [project?.id]);

  useEffect(() => {
    if (!invoiceId && pickableInvoices[0]) setInvoiceId(pickableInvoices[0].id);
  }, [pickableInvoices, invoiceId]);

  const activeInvoice = pickableInvoices.find((i) => i.id === invoiceId);
  const cats = DOC_CATEGORIES.filter((c) => !c.ppnOnly || activeInvoice?.ppn_type === "ppn");
  const activeCategory = cats.some((c) => c.key === category) ? category : (cats[0]?.key || "lainnya");

  const rows = useMemo(() => {
    const out = [];
    (project?.prospect_attachments || []).forEach((a) => {
      const u = a.uploaded_by ? userMap[a.uploaded_by] : null;
      out.push({
        id: `p-${a.id}`, source: "project", title: a.filename, size: a.size,
        date: a.uploaded_at, who: (u?.nickname || u?.name) || a.uploaded_by_name || null, pic: u?.picture,
        onOpen: () => onOpenProject?.(a.id),
        onDelete: canEdit ? (e) => onDeleteProject?.(a, e) : null,
      });
    });
    quotations.forEach((q) => {
      out.push({
        id: `q-${q.id}`, source: "quotation", title: q.number, mono: true,
        sub: `${fmtDate(q.date)} · ${fmtIDR(q.grand_total)} · ${q.ppn_type === "ppn" ? "PPN 11%" : "Non-PPN"}`,
        date: q.date, who: null,
        onOpen: () => {
          const token = localStorage.getItem("sp_token");
          window.open(`${API}/quotations/${q.id}/pdf?auth=${encodeURIComponent(token || "")}`, "_blank");
        },
        onDelete: null,
      });
    });
    pos.forEach((po) => {
      out.push({
        id: `po-${po.id}`, source: "po", title: po.number, mono: true,
        sub: `${po.vendor_name} · ${fmtDate(po.date)} · ${fmtIDR(po.grand_total)}`,
        date: po.date, who: null,
        onOpen: () => {
          const token = localStorage.getItem("sp_token");
          window.open(`${API}/purchase-orders/${po.id}/pdf?auth=${encodeURIComponent(token || "")}`, "_blank");
        },
        onDelete: null,
      });
    });
    poFiles.forEach((a) => {
      out.push({
        id: `pof-${a.id}`, source: "po", title: a.filename, size: a.size,
        date: a.uploaded_at, who: a.uploaded_by_name || null,
        tags: [a.po_number].filter(Boolean),
        onOpen: () => {
          const token = localStorage.getItem("sp_token");
          window.open(`${API}/purchase-orders/${a.purchase_order_id}/attachments/${a.id}/download?auth=${encodeURIComponent(token || "")}`, "_blank");
        },
        onDelete: null,
      });
    });
    invoiceAttachments.forEach((a) => {
      out.push({
        id: `i-${a.id}`, source: "invoice", title: a.filename, size: a.size,
        date: a.uploaded_at, who: a.uploaded_by_name || null,
        tags: [a.invoice_number, catLabel(a.category)].filter(Boolean),
        onOpen: () => onOpenInvoiceDoc?.(a),
        onDelete: canDeleteInvoiceDoc ? () => onDeleteInvoiceDoc?.(a) : null,
      });
    });
    return out;
  }, [project, quotations, pos, poFiles, invoiceAttachments, userMap, canEdit, canDeleteInvoiceDoc, onOpenProject, onDeleteProject, onOpenInvoiceDoc, onDeleteInvoiceDoc]);

  const counts = {
    all: rows.length,
    project: rows.filter((r) => r.source === "project").length,
    quotation: rows.filter((r) => r.source === "quotation").length,
    invoice: rows.filter((r) => r.source === "invoice").length,
    po: rows.filter((r) => r.source === "po").length,
  };
  const order = ["project", "quotation", "invoice", "po"];
  const visibleGroups = order
    .filter((s) => filter === "all" || filter === s)
    .map((s) => ({ source: s, items: rows.filter((r) => r.source === s) }))
    .filter((g) => g.items.length > 0);

  const uploadInvoiceFiles = async (files) => {
    if (!files?.length || !invoiceId) return;
    setBusy(true);
    try { await onUploadInvoice?.(invoiceId, activeCategory, files); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-3" data-testid="project-attachments-board">
      {/* HEADER: filter chips + upload */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex items-center gap-1.5 flex-wrap flex-1 min-w-0">
          {[["all", "Semua"], ["project", "Proyek"], ["quotation", "Quotation"], ["invoice", "Invoice"], ["po", "PO"]].map(([key, label]) => (
            <button
              key={key} type="button" onClick={() => setFilter(key)}
              data-testid={`attachment-filter-${key}`}
              className={`h-8 px-3 rounded-full text-[11px] font-semibold border transition-all inline-flex items-center gap-1.5 ${
                filter === key ? "bg-[#090808] text-white border-[#090808]" : "bg-white text-[#626061] border-[#DCE0E7] hover:border-[#3B4A63]"
              }`}
            >
              {label}
              <span className={`font-num tabular-nums ${filter === key ? "text-white/70" : "text-[#A0A3A8]"}`}>{counts[key]}</span>
            </button>
          ))}
        </div>
        {canEdit && (
          <button
            type="button" onClick={() => setPanelOpen((v) => !v)} data-testid="attachment-upload-toggle"
            className="h-8 px-3.5 rounded-full bg-gradient-to-r from-[#6B8FE2] to-[#3B4A63] text-white text-[11px] font-semibold inline-flex items-center gap-1.5 shrink-0"
          >
            <Plus className="w-3.5 h-3.5" /> Upload
            <ChevronDown className={`w-3 h-3 transition-transform ${panelOpen ? "rotate-180" : ""}`} />
          </button>
        )}
      </div>

      {/* UPLOAD PANEL */}
      {canEdit && panelOpen && (
        <div className="rounded-2xl border border-[#B0C7EA]/70 bg-[#F5F9FF]/70 p-4 space-y-3" data-testid="attachment-upload-panel">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-widest text-[#3B4A63] mb-1.5">Tujuan Dokumen</div>
            <div className="flex items-center gap-1.5">
              {[["project", "Lampiran Proyek"], ["invoice", "Dokumen Invoice"]].map(([key, label]) => (
                <button
                  key={key} type="button" onClick={() => setTarget(key)} data-testid={`attachment-target-${key}`}
                  className={`h-8 px-3 rounded-full text-[11px] font-semibold border transition-all ${
                    target === key ? "bg-[#3B4A63] text-white border-[#3B4A63]" : "bg-white text-[#626061] border-[#DCE0E7] hover:border-[#3B4A63]"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          {target === "invoice" && pickableInvoices.length === 0 ? (
            <div className="rounded-xl bg-[#FFF7E8] border border-[#F2D9A0] p-3 text-[12px] text-[#8C6318] flex items-center gap-2">
              <FileText className="w-4 h-4 shrink-0" /> Buat invoice untuk proyek ini terlebih dahulu agar dokumen bisa dilampirkan.
            </div>
          ) : target === "invoice" ? (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-widest text-[#3B4A63] mb-1.5">Invoice</div>
                  <Select value={invoiceId} onValueChange={setInvoiceId}>
                    <SelectTrigger data-testid="attachment-invoice-select" className="h-10 rounded-xl bg-white border-[#DCE0E7]"><SelectValue placeholder="Pilih invoice" /></SelectTrigger>
                    <SelectContent>
                      {pickableInvoices.map((i) => (
                        <SelectItem key={i.id} value={i.id}>
                          <span className="font-num text-xs">{i.number}</span>
                          <span className="text-[10px] text-[#A0A3A8] ml-2">{i.ppn_type === "ppn" ? "PPN" : "Non-PPN"}</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-widest text-[#3B4A63] mb-1.5">Jenis Dokumen</div>
                  <div className="flex flex-wrap items-center gap-1.5">
                    {cats.map((c) => (
                      <button
                        key={c.key} type="button" onClick={() => setCategory(c.key)} data-testid={`attachment-cat-${c.key}`}
                        className={`h-8 px-3 rounded-full text-[11px] font-semibold border transition-all ${
                          activeCategory === c.key ? "bg-[#3B4A63] text-white border-[#3B4A63]" : "bg-white text-[#626061] border-[#DCE0E7] hover:border-[#3B4A63]"
                        }`}
                      >
                        {c.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              <input
                ref={invoiceFileRef} type="file" multiple className="hidden"
                accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.doc,.docx,.xls,.xlsx,.csv,.txt"
                onChange={(e) => { uploadInvoiceFiles(e.target.files); e.target.value = ""; }}
                data-testid="attachment-invoice-file-input"
              />
              <button
                type="button" onClick={() => invoiceFileRef.current?.click()} disabled={busy || !invoiceId}
                data-testid="attachment-invoice-upload-btn"
                className="w-full h-11 rounded-full bg-[#090808] text-white text-sm font-semibold hover:bg-black inline-flex items-center justify-center gap-2 disabled:opacity-60"
              >
                <Paperclip className="w-4 h-4" /> {busy ? "Mengupload…" : `Pilih file — ${catLabel(activeCategory)}`}
              </button>
            </>
          ) : (
            <>
              <input
                ref={projectFileRef} type="file" multiple className="hidden"
                accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.doc,.docx,.xls,.xlsx,.csv,.txt"
                onChange={(e) => { onUploadProject?.(e.target.files); e.target.value = ""; }}
                data-testid="attachment-project-file-input"
              />
              <button
                type="button" onClick={() => projectFileRef.current?.click()} disabled={attaching || !project?.prospect_id}
                data-testid="attachment-project-upload-btn"
                className="w-full h-11 rounded-full bg-[#090808] text-white text-sm font-semibold hover:bg-black inline-flex items-center justify-center gap-2 disabled:opacity-60"
              >
                <Paperclip className="w-4 h-4" /> {attaching ? "Mengupload…" : "Pilih file lampiran proyek"}
              </button>
            </>
          )}
          <div className="text-[10px] text-[#A0A3A8] text-center">PDF · DOC · XLS · Gambar · maks 15MB per file</div>
        </div>
      )}

      {/* LIST */}
      {visibleGroups.length === 0 ? (
        <div className="rounded-2xl bg-white/50 border border-[#DCE0E7]/60 p-8 text-center text-sm text-[#A0A3A8]" data-testid="attachment-empty">
          Belum ada dokumen pada kategori ini.
        </div>
      ) : (
        <div className="rounded-2xl border border-[#DCE0E7]/70 bg-white overflow-hidden">
          {visibleGroups.map((g) => {
            const sm = SOURCE_META[g.source];
            return (
              <div key={g.source} data-testid={`attachment-group-${g.source}`}>
                <div className="px-4 py-2 bg-[#F7F8FA] border-y border-[#DCE0E7]/60 first:border-t-0 flex items-center gap-2">
                  <span className="text-[9px] uppercase tracking-widest font-bold text-[#626061]">{sm.label}</span>
                  <span className="text-[9px] font-num tabular-nums text-[#A0A3A8]">{g.items.length}</span>
                  {g.source === "quotation" && <span className="text-[9px] text-[#A0A3A8]">· otomatis dari sistem</span>}
                </div>
                <div className="divide-y divide-[#DCE0E7]/60">
                  {g.items.map((r) => {
                    const ic = fileTint(r.mono ? "x.pdf" : r.title);
                    return (
                      <div key={r.id} data-testid={`attachment-row-${r.id}`} className="px-4 py-3 flex items-center gap-3 hover:bg-[#FAFBFC] transition">
                        <div className={`w-10 h-10 rounded-xl ${ic.bg} ${ic.fg} flex items-center justify-center font-bold text-[10px] shrink-0`}>{ic.label}</div>
                        <div className="flex-1 min-w-0">
                          <div className={`text-[13px] font-semibold text-[#090808] truncate ${r.mono ? "font-num tabular-nums" : ""}`}>{r.title}</div>
                          <div className="text-[11px] text-[#A0A3A8] mt-0.5 flex items-center gap-1.5 flex-wrap">
                            {(r.tags || []).map((t) => (
                              <span key={t} className="inline-flex items-center h-4 px-1.5 rounded-full text-[8px] font-bold uppercase tracking-widest bg-[#F4F5F7] text-[#626061] border border-[#DCE0E7] font-num">{t}</span>
                            ))}
                            {r.sub && <span className="truncate">{r.sub}</span>}
                            {r.who && (
                              <span className="inline-flex items-center gap-1 truncate">
                                <Avatar name={r.who} picture={r.pic} size="sm" className="!w-3.5 !h-3.5 !text-[8px] !border-0 shrink-0" />
                                {r.who}
                              </span>
                            )}
                            {r.date && !r.sub && <span className="font-num tabular-nums">{fmtDate(r.date)}</span>}
                            {r.size ? <span className="font-num tabular-nums">{Math.max(1, Math.round(r.size / 1024))} KB</span> : null}
                          </div>
                        </div>
                        <button onClick={r.onOpen} title="Buka / Unduh" data-testid={`attachment-open-${r.id}`} className="w-8 h-8 rounded-full hover:bg-[#F4F5F7] text-[#3B4A63] flex items-center justify-center shrink-0">
                          <Download className="w-3.5 h-3.5" />
                        </button>
                        {r.onDelete && (
                          <button onClick={r.onDelete} title="Hapus" data-testid={`attachment-delete-${r.id}`} className="w-8 h-8 rounded-full hover:bg-[#FDE7E9] text-[#CE6968] flex items-center justify-center shrink-0">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
